import { Alert, Badge, Button, Loader, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useDeriveFrn } from 'src/secret-menu/smart-create/hooks/useDeriveFrn';

type Result = { frn: string | null; companyName: string | null };

export default function DeriveFrnTestTab() {
  const { deriveFrn, isDerivingFrn } = useDeriveFrn();
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    initialValues: { websiteUrl: '' },
    validate: {
      websiteUrl: v => (v.trim().length === 0 ? 'Website URL is required' : null),
    },
  });

  const handleSubmit = form.onSubmit(async values => {
    setResult(null);
    setError(null);
    try {
      const response = await deriveFrn({ websiteUrl: values.websiteUrl });
      setResult(response.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    }
  });

  return (
    <Stack gap="lg" maw={560} p="md" bg="white">
      <Stack gap={4}>
        <Text size="displayXs" fw={FONT_WEIGHTS.semibold}>
          FRN derivation test
        </Text>
        <Text size="textMd" c="gray.6">
          Enter a financial adviser website URL to attempt FRN extraction.
        </Text>
      </Stack>

      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          <TextInput
            label="Website URL"
            placeholder="e.g. https://example.co.uk"
            {...form.getInputProps('websiteUrl')}
          />
          <Button
            type="submit"
            disabled={isDerivingFrn}
            leftSection={isDerivingFrn ? <Loader size="xs" /> : undefined}
            w="fit-content"
          >
            Derive FRN
          </Button>
        </Stack>
      </form>

      {error && (
        <Alert color="red" title="Error">
          {error}
        </Alert>
      )}

      {result && (
        <Stack gap="xs">
          {result.frn ? (
            <>
              <Text size="textSm" c="gray.6">
                FRN found and validated against FCA register
              </Text>
              <Badge color="green" size="lg">
                FRN: {result.frn}
              </Badge>
              {result.companyName && (
                <Text size="textMd" fw={FONT_WEIGHTS.medium}>
                  {result.companyName}
                </Text>
              )}
            </>
          ) : (
            <Alert color="yellow" title="Not found">
              No FRN detected on this website.
            </Alert>
          )}
        </Stack>
      )}
    </Stack>
  );
}
